/**
 * AMD site-footer content.
 *
 * Transcribed from the legacy `components/layout/Footer.vue` TEMPLATE, which is
 * the only source of truth there: that file also carries `socialLinks`, `menus`
 * and `footerLinks` arrays in `data()` that are never bound to anything and
 * disagree with the rendered markup (wrong labels, missing `www.`, a different
 * Authorized Distributors URL). Those are dead — ignore them if you ever diff
 * against the Vue file.
 *
 * Kept as data rather than markup so the footer renders from one list and a
 * link change is a one-line edit.
 */

/** The social / contact row. `icon` is a key into components/common/socialIcons. */
export const FOOTER_SOCIAL = Object.freeze([
  Object.freeze({
    id: 'facebook',
    label: 'AMD on Facebook',
    href: 'https://www.facebook.com/amd',
    icon: 'facebook',
  }),
  Object.freeze({
    id: 'instagram',
    label: 'AMD on Instagram',
    href: 'https://www.instagram.com/amd',
    icon: 'instagram',
  }),
  Object.freeze({
    id: 'linkedin',
    label: 'AMD on LinkedIn',
    href: 'https://www.linkedin.com/company/amd',
    icon: 'linkedin',
  }),
  Object.freeze({
    id: 'twitch',
    label: 'AMD on Twitch',
    href: 'https://www.twitch.tv/amd',
    icon: 'twitch',
  }),
  Object.freeze({ id: 'x', label: 'AMD on X', href: 'https://twitter.com/amd', icon: 'x' }),
  Object.freeze({
    id: 'youtube',
    label: 'AMD on YouTube',
    href: 'https://www.youtube.com/user/amd?sub_confirmation=1',
    icon: 'youtube',
  }),
  Object.freeze({
    id: 'preferences',
    label: 'Email preferences',
    href: 'https://www.amd.com/en/preferences.html',
    icon: 'envelope',
  }),
]);

/**
 * The five link columns.
 *
 * Note: legacy renders a SEPARATE list in its mobile accordion that differs
 * from the desktop grid — Company loses "Corporate Responsibility", Partners
 * gains "Partner Resource Library" and uses a different Authorized Distributors
 * URL. That divergence is a bug, not a feature; this is the desktop list, used
 * at every width.
 */
export const FOOTER_COLUMNS = Object.freeze([
  Object.freeze({
    heading: 'Company',
    links: Object.freeze([
      { label: 'About AMD', href: 'https://www.amd.com/en/corporate.html' },
      { label: 'Management Team', href: 'https://www.amd.com/en/corporate/leadership.html' },
      {
        label: 'Corporate Responsibility',
        href: 'https://www.amd.com/en/corporate/corporate-responsibility.html',
      },
      { label: 'Careers', href: 'https://careers.amd.com/careers-home' },
      { label: 'Contact Us', href: 'https://www.amd.com/en/corporate/contacts.html' },
    ]),
  }),
  Object.freeze({
    heading: 'News & Events',
    links: Object.freeze([
      { label: 'Newsroom', href: 'https://www.amd.com/en/newsroom.html' },
      { label: 'Events', href: 'https://www.amd.com/en/corporate/events.html' },
      { label: 'Blogs', href: 'https://community.amd.com/t5/blogs/ct-p/amd-blogs' },
      { label: 'Media Library', href: 'https://www.amd.com/en/newsroom/media-library.html' },
    ]),
  }),
  Object.freeze({
    heading: 'Community',
    links: Object.freeze([
      { label: 'Support', href: 'https://community.amd.com/t5/support-forums/ct-p/supprtforums' },
      { label: 'Developer', href: 'https://community.amd.com/t5/developers/ct-p/devgurus' },
      { label: 'Red Team', href: 'https://community.amd.com/t5/red-team/ct-p/red-team' },
    ]),
  }),
  Object.freeze({
    heading: 'Partners',
    links: Object.freeze([
      { label: 'Developers Center', href: 'https://www.amd.com/en/developer.html' },
      { label: 'AMD Partner Hub', href: 'https://www.amd.com/en/partner.html' },
      {
        label: 'Authorized Distributors',
        href: 'https://www.amd.com/en/partner/about-partners/authorized-distributors.html',
      },
      {
        label: 'AMD University Program',
        href: 'https://www.amd.com/en/corporate/university-program.html',
      },
    ]),
  }),
  Object.freeze({
    heading: 'Investors',
    links: Object.freeze([
      { label: 'Investor Relations', href: 'https://ir.amd.com/' },
      { label: 'Financial Information', href: 'https://ir.amd.com/financial-information' },
      {
        label: 'Board of Directors',
        href: 'https://ir.amd.com/leadership-governance/board-of-directors',
      },
      {
        label: 'Governance Documents',
        href: 'https://ir.amd.com/leadership-governance/governance-documents',
      },
      { label: 'SEC Filings', href: 'https://ir.amd.com/sec-filings' },
    ]),
  }),
]);

/**
 * The pipe-separated legal row.
 *
 * "Cookie Settings" is not a navigation link: it carries the OneTrust hook
 * (`#cookiessettings` + the `ot-sdk-show-settings` class) that opens the
 * consent dialog. It keeps both so the banner can bind to it if OneTrust is
 * ever added, and is marked `external: false` so it does not open a new tab.
 */
export const FOOTER_LEGAL = Object.freeze([
  { label: 'Terms and Conditions', href: 'https://www.amd.com/en/legal/copyright.html' },
  { label: 'Privacy (Updated)', href: 'https://www.amd.com/en/legal/privacy.html' },
  { label: 'Trademarks', href: 'https://www.amd.com/en/legal/trademarks.html' },
  {
    label: 'Supply Chain Transparency',
    href: 'https://www.amd.com/content/dam/amd/en/documents/corporate/cr/supply-chain-transparency.pdf',
  },
  { label: 'Fair & Open Competition', href: 'https://www.amd.com/en/legal/competition.html' },
  {
    label: 'UK Tax Strategy',
    href: 'https://www.amd.com/content/dam/amd/en/documents/corporate/amd-uk-tax-strategy.pdf',
  },
  { label: 'Cookies Policy', href: 'https://www.amd.com/en/legal/cookies.html' },
  Object.freeze({
    label: 'Cookie Settings',
    href: '#cookiessettings',
    external: false,
    className: 'ot-sdk-show-settings',
  }),
]);
